import { useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { RemindingTone, useUpdateUserMutation, useUserQuery } from '../../api/gql';
import { FullSizeLoader } from '../../components/FullSizeLoader';
import { PageHeader } from '../../components/PageHeader';
import { RemindingTones } from '../../components/RemindingTones';
import { PageContent, PageLayout } from '../../layout';
import { notify } from '../../utils/notification';

export const RemindingTonePage = () => {
  const { t } = useTranslation();
  const [selectedRemindingToneLoading, setSelectedRemindingToneLoading] = useState<RemindingTone | null>(null);
  const queryClient = useQueryClient();
  const { data, isLoading } = useUserQuery();
  const { mutate } = useUpdateUserMutation({
    onSuccess: (res) => {
      notify.positive({ message: t('remindingTone.change.success') });
      queryClient.setQueryData(useUserQuery.getKey(), {
        user: res.updateUser,
      });
    },
    onError: (error) => {
      notify.negative({ message: t('remindingTone.change.error') });
      console.error(error);
    },
    onSettled: () => {
      setSelectedRemindingToneLoading(null);
    },
  });

  const onToneSelect = (remindingTone: RemindingTone) => {
    setSelectedRemindingToneLoading(remindingTone);
    mutate({
      input: {
        defaultRemindingTone: remindingTone,
      },
    });
  };

  return (
    <PageLayout>
      <PageHeader title={t('remindingTone.title')} backRouteTo={null} />
      <Box pt={{ md: 4, xs: 2 }} display={'flex'} justifyContent={'center'}>
        <Alert severity={'info'} sx={{ whiteSpace: 'break-spaces' }}>
          {t('remindingTone.subtitle')}
        </Alert>
      </Box>
      <PageContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          pt: { md: 8, xs: 2 },
          textAlign: { xs: 'center', md: 'left' },
        }}
      >
        {isLoading ? <FullSizeLoader /> : null}
        <Typography variant={'h1'} component={'h2'} color={'text.secondary'} sx={{ pb: { md: 8, xs: 6 } }}>
          {t('prefillAutomatically')}
        </Typography>
        <RemindingTones
          onToneChange={onToneSelect}
          selected={data?.user.defaultRemindingTone}
          remindingToneLoading={selectedRemindingToneLoading ?? undefined}
        />
      </PageContent>
    </PageLayout>
  );
};
